import { Command, CommandList } from '@nest/internal-frontend';
import { Button, Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@nest/uikit-react';
import { ArrowLeft, Menu } from 'lucide-react';
import { ReactNode, useCallback, useState, type UIEvent } from 'react';
import { useNavigate } from 'react-router';

const TopBar = ({
	children,
	showBackButton,
	hideBackButton,
	backButtonTo,
	customBackButton,
	commands,
	onCommandsOpenChange,
	elevated
}: {
	children: ReactNode;
	showBackButton?: boolean;
	hideBackButton?: boolean;
	backButtonTo?: string;
	customBackButton?: ReactNode;
	commands?: Command[];
	onCommandsOpenChange: (open: boolean) => void;
	elevated?: boolean;
}) => {
	const navigate = useNavigate();
	const [executingCommands, setExecutingCommands] = useState<Set<string>>(new Set());

	const handleCommandClick = async (commandId: string, run: () => void | Promise<void>) => {
		setExecutingCommands(prev => new Set(prev).add(commandId));
		try {
			const result = run();
			if (result instanceof Promise) {
				await result;
			}
		} finally {
			setExecutingCommands(prev => {
				const newSet = new Set(prev);
				newSet.delete(commandId);
				return newSet;
			});
		}
		onCommandsOpenChange(false);
	};

	const shouldShowDefaultBackButton = showBackButton && !hideBackButton;

	const goToBack = useCallback(() => {
		if (backButtonTo) {
			navigate(backButtonTo);
		} else {
			navigate(-1);
		}
	}, [backButtonTo, navigate]);

	return (
		<div className={`flex justify-between relative items-center p-2 bg-black text-white ${elevated ? 'shadow-md' : ''}`}>
			{shouldShowDefaultBackButton && (
				<Button variant="ghost" size="icon" onClick={goToBack}>
					<ArrowLeft className="h-4 w-4" />
				</Button>
			)}
			{customBackButton && <div className="flex-shrink-0">{customBackButton}</div>}
			<div className="flex-1 flex items-center font-bold">{children}</div>
			{commands && commands.length > 0 && (
				<Sheet onOpenChange={onCommandsOpenChange}>
					<SheetTrigger asChild>
						<Button variant="ghost" size="icon">
							<Menu className="h-4 w-4" />
						</Button>
					</SheetTrigger>
					<SheetContent side="bottom">
						<SheetHeader>
							<SheetTitle>Možnosti</SheetTitle>
						</SheetHeader>
						<div className="flex flex-col gap-2 p-2">
							{commands.map(command => {
								const { run, disabled } = command;
								const isDisabled = disabled === true || (Array.isArray(disabled) && disabled.length > 0);
								const isExecuting = executingCommands.has(command.id);

								// Dynamicky aktualizuj label a ikonu pro theme toggle
								let displayLabel = command.label;
								let displayIcon = command.icon;

								// if (command.id === 'theme-toggle' && theme) {
								// 	const isDark = theme === 'dark';
								// 	displayLabel = isDark ? 'Přepnout na světlé téma' : 'Přepnout na tmavé téma';
								// 	displayIcon = isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />;
								// }

								return (
									<Button
										key={command.id}
										variant="outline"
										className="w-full justify-start"
										onClick={() => handleCommandClick(command.id, run)}
										disabled={isDisabled || isExecuting}
										loading={isExecuting}
									>
										{displayIcon}
										<span className="ml-2">{displayLabel}</span>
									</Button>
								);
							})}
						</div>
					</SheetContent>
				</Sheet>
			)}
		</div>
	);
};

export const TerminalScreen = ({
	children,
	topBar,
	showBackButton,
	backButtonTo,
	hideBackButton,
	customBackButton,
	commands,
	bottomContent
}: {
	children: ReactNode;
	topBar: ReactNode;
	showBackButton?: boolean;
	backButtonTo?: string;
	hideBackButton?: boolean;
	customBackButton?: ReactNode;
	commands?: CommandList<any>;
	bottomContent?: ReactNode;
}) => {
	const [, setIsCommandsOpen] = useState(false);
	const [isContentScrolled, setIsContentScrolled] = useState(false);

	const handleContentScroll = useCallback((event: UIEvent<HTMLDivElement>) => {
		const target = event.currentTarget;
		const isScrolled = target.scrollTop > 0;
		setIsContentScrolled(isScrolled);
	}, []);

	return (
		<div className="flex flex-col h-screen w-screen fixed top-0 left-0 min-h-screen min-w-screen max-h-screen max-w-screen">
			<TopBar
				showBackButton={showBackButton}
				hideBackButton={hideBackButton}
				customBackButton={customBackButton}
				commands={commands}
				onCommandsOpenChange={setIsCommandsOpen}
				backButtonTo={backButtonTo}
				elevated={isContentScrolled}
			>
				{topBar}
			</TopBar>
			<div className="flex-1 bg-background overflow-y-auto h-full" onScroll={handleContentScroll}>
				{children}
			</div>
			{bottomContent && <div className="p-2 bg-[#F5F7F9] border-t border-border">{bottomContent}</div>}
		</div>
	);
};
